// 지역 6 × 스테이지 10, 마을 6 (정본: docs/CONTENT-90D-PRD.md 2절). 데이터만 — 게임 연결은 c003.
import type { DropTableDef, DropTableId, RegionDef, RegionNo, StageDef, StageId, StageWave, TownDef } from './types';
import { REGIONS } from '../regions';
import { STAGE_STEP } from '../growth';

/** 스테이지 HP·피해·처치 EXP 배율(지역 내): 1 + 0.12×(s−1). 정본은 shared/growth.STAGE_STEP(처치 EXP와 같은 값) */
export { STAGE_STEP };
export const STAGES_PER_REGION = 10;
export const MID_BOSS_STAGE = 5;
export const FINAL_BOSS_STAGE = 10;
/** 처치 EXP 지역 배율 밑(시뮬 결정: 스테이지 배율 끝값 2.08보다 커야 지역 전환 때 EXP가 줄지 않는다) */
export const REGION_XP_BASE = 2.2;
/** 난이도 배율(PRD 2.1, 시뮬 XP 배율) */
export const DIFFICULTY = {
  normal: { hp: 1, dmg: 1, xp: 1, mobLevelAdd: 0 },
  hard: { hp: 3, dmg: 2, xp: 2.3, mobLevelAdd: 10 },
  hell: { hp: 6.5, dmg: 3.5, xp: 5, mobLevelAdd: 20 },
} as const;
export const MOB_LEVEL_MAX = 99;
/**
 * 지역 몹 HP·피해 기준 배율(지역1 = 1, 같은 지역 안에서는 스테이지 배율 1~2.08을 더 곱한다). D0a 피해 시뮬 확정(2026-10-22):
 * 그 지역에 들어서는 기대 영웅(레벨·장비 등급·강화)이 1스테이지 일반 몹을 같은 타수로 잡게 영웅 평균 타격 성장에서 역산했다.
 * PRD 2.1 가안 "이전 지역 10스테이지 ×1.15"(지역당 ×2.39)는 장비 성장이 지역마다 달라 쓰지 않는다(sim-report 피해 절).
 * 기존 단일 맵 regions.ts R2 mobHpMult는 c009에서 이 표의 지역2 값(2.3)으로 맞췄다(content.test가 같은지 확인).
 */
export const REGION_HP_MULT: readonly number[] = [1, 2.3, 4.9, 10.2, 21.5, 45];
/** 보스 HP = 그 스테이지 일반 몹 평균 HP × 배율. 기존 1지역 보스 6,400 ≈ 지역1 몹 평균 HP의 약 46배 */
export const BOSS_HP_RATIO = { mid: 40, final: 70 } as const;
/**
 * 지역 몹 레벨 = 그 지역 권장 구간 끝 + 난이도 가산(하드 +10, 헬 +20, 최대 99).
 * 레벨 차 보정(SKILL-TREE 1절, 5↑ ×0.5·10↑ ×0.1)의 기준. 시뮬도 같은 값을 쓴다(c003 리뷰 P1).
 */
export function mobLevel(region: RegionNo, diff: keyof typeof DIFFICULTY): number {
  const r = REGIONS_90D[region - 1];
  return Math.min(MOB_LEVEL_MAX, r.levels[1] + DIFFICULTY[diff].mobLevelAdd);
}

/**
 * 웨이브 가안(LIVE-FUN 3.0b: 한 스테이지 = 방 하나, 웨이브 2~3). 스테이지가 오를수록 마릿수·엘리트가 는다. 실제 배치는 c-stage 사이클에서 맞춘다.
 * 4번째 칸(원거리형)은 지역2부터.
 */
function wavesFor(region: RegionNo, s: number, kind: StageDef['kind']): StageWave[] {
  const n = 3 + Math.floor(s / 2);
  const ranged = region >= 2 ? 1 + Math.floor(s / 4) : 0;
  const w = (k: number, el: number): StageWave => ({ counts: [n + k, Math.max(1, Math.floor(n / 3) + k), Math.max(1, Math.floor(n / 4)), ranged], elites: el });
  if (kind === 'final') return [w(1, 1), w(2, 2)];
  if (kind === 'mid') return [w(0, 1), w(1, 1)];
  return s >= 7 ? [w(0, 0), w(1, 1), w(2, 1)] : s >= 3 ? [w(0, 0), w(1, 1)] : [w(0, 0), w(1, 0)];
}

function stages(region: RegionNo, levels: readonly [number, number]): StageDef[] {
  const out: StageDef[] = [];
  for (let s = 1; s <= STAGES_PER_REGION; s++) {
    const id = `r${region}-s${String(s).padStart(2, '0')}` as StageId;
    const kind = s === FINAL_BOSS_STAGE ? 'final' : s === MID_BOSS_STAGE ? 'mid' : 'normal';
    const rec = Math.round(levels[0] + ((levels[1] - levels[0]) * (s - 1)) / (STAGES_PER_REGION - 1));
    const mul = Math.round((1 + STAGE_STEP * (s - 1)) * 100) / 100;
    out.push({ id, region, index: s, kind, hpMul: mul, dmgMul: mul, recLevel: rec, waves: wavesFor(region, s, kind), drops: `drop_${kind}` as DropTableId });
  }
  return out;
}

/**
 * 드롭 표(PRD 4.2: 일반 몹 확률, 엘리트·보스 확정, 전설은 지역보스·레이드 전용). 장비 확률·등급은 가안(c007 드롭 시뮬로 확정).
 * stoneChance는 D0a 재료 시뮬 확정(2026-10-22): 처치당 강화석 1.5~4%(이전 8~12%는 +12가 21일차에 끝나 재료가 묶이지 않았다).
 * 등급 풀 분리(c009): gradeWeights = 일반 웨이브(몹·엘리트), bossGradeWeights = 보스 확정 드롭. 전설은 지역보스 풀에만 둔다 —
 * 이전 단일 풀(지역보스 스테이지 전체 2%)은 일반 웨이브도 전설을 굴려 62일차 기대 전설이 약 15개였다. 보스 풀 3%로 62일 약 3.6개·90일 약 6.8개(레이드 포함).
 */
export const DROP_TABLES: Readonly<Record<DropTableId, DropTableDef>> = {
  // BALANCE-1028 2절(대표님 "에픽(보라)이 너무 잘 뜬다"): 영웅 가중 일반 6→0.3·중간보스 판 18→1.5(보스 확정 18→12)·지역보스 판 28→3(보스 확정 45→20).
  // 한 판 기대 영웅: 일반 스테이지 1.2 → 0.06개, 지역보스 처치 0.9 → 0.4개. 장비 수(equipChance·확정 수)는 그대로 두고 등급만 내렸다(판매 경제 유지).
  // 영웅 분해로 나오던 강화석(처치당 약 0.006·0.021·0.032개, 경력 시뮬 골드 순수입이 음수가 되지 않게 0.006·0.019·0.028로 맞춤)은 salvageStone(처치당 강화석 1개 확률, 난이도 배율 없음 — 분해 강화석도 난이도와 무관했다)으로 옮겼다.
  // stoneChance에 얹으면 헬 ×4.8 배율까지 받아 후반 강화가 앞당겨지고 골드가 바닥난다(경력 시뮬 순수입 음수)
  drop_normal: { id: 'drop_normal', equipChance: 0.04, guaranteed: 0, gradeWeights: { common: 80, rare: 19.7, epic: 0.3, set: 0, legend: 0 }, bossGradeWeights: { common: 80, rare: 19.7, epic: 0.3, set: 0, legend: 0 }, stoneChance: 0.015, salvageStone: 0.006, eliteGuaranteed: 1 },
  drop_mid: { id: 'drop_mid', equipChance: 0.05, guaranteed: 1, gradeWeights: { common: 55, rare: 42.5, epic: 1.5, set: 1, legend: 0 }, bossGradeWeights: { common: 25, rare: 60, epic: 12, set: 3, legend: 0 }, stoneChance: 0.025, salvageStone: 0.019, eliteGuaranteed: 1 },
  drop_final: { id: 'drop_final', equipChance: 0.05, guaranteed: 2, gradeWeights: { common: 40, rare: 53, epic: 3, set: 4, legend: 0 }, bossGradeWeights: { common: 0, rare: 62, epic: 20, set: 15, legend: 3 }, stoneChance: 0.04, salvageStone: 0.028, eliteGuaranteed: 1 },
};

function region(def: Omit<RegionDef, 'stages' | 'xpMult'>): RegionDef {
  return { ...def, xpMult: Math.round(Math.pow(REGION_XP_BASE, def.no - 1) * 1000) / 1000, stages: stages(def.no, def.levels) };
}

export const REGIONS_90D: readonly RegionDef[] = [
  region({ no: 1, legacyId: 'r1', name: '이끼 유적', theme: '이끼 낀 고대 유적', mobs: REGIONS.r1.mobNames, midBoss: '고블린 족장', finalBoss: '고목 수호자', travel: 'gate-walk', nextTown: 2, levels: [1, 18] }),
  region({ no: 2, legacyId: 'r2', name: '잿불 협곡', theme: '용암 협곡', mobs: REGIONS.r2.mobNames, midBoss: '용암 골렘', finalBoss: '잿불 대주술사', travel: 'ship', nextTown: 3, levels: [18, 30] }),
  region({ no: 3, legacyId: 'r3', name: '서리 묘지', theme: '얼어붙은 고딕 묘지', mobs: REGIONS.r3.mobNames, midBoss: '묘지기 기사', finalBoss: '서리 리치', travel: 'cutin-walk', nextTown: 4, levels: [30, 40] }),
  region({ no: 4, legacyId: 'r4', name: '가라앉은 사원', theme: '독안개 늪 사원', mobs: REGIONS.r4.mobNames, midBoss: '독안개 사제', finalBoss: '늪의 히드라', travel: 'cutin', nextTown: 5, levels: [40, 55] }),
  region({ no: 5, legacyId: 'r5', name: '황금 모래 폐도', theme: '사막 고대 도시', mobs: REGIONS.r5.mobNames, midBoss: '경비대장', finalBoss: '모래 군주', travel: 'cutin', nextTown: 6, levels: [55, 68] }),
  region({ no: 6, legacyId: 'r6', name: '심연의 성채', theme: '허공 위 암흑 성채', mobs: REGIONS.r6.mobNames, midBoss: '심연 집행자', finalBoss: '심연의 군주', travel: 'ending', nextTown: 6, levels: [68, 80] }),
];

export const TOWNS_90D: readonly TownDef[] = [
  { no: 1, name: '시작 마을', opens: ['shop', 'forge', 'trainer', 'board', 'stash'], shopTier: 1, enhanceCap: 5 },
  { no: 2, name: '잿불 전초기지', opens: ['warp', 'pet', 'raid'], shopTier: 2, enhanceCap: 9 },
  { no: 3, name: '서리 항구', opens: ['jeweler'], shopTier: 3, enhanceCap: 9 },
  { no: 4, name: '늪지 장터', opens: ['alchemist'], shopTier: 4, enhanceCap: 12 },
  { no: 5, name: '오아시스 대상단', opens: [], shopTier: 5, enhanceCap: 12 },
  { no: 6, name: '하늘 요새', opens: [], shopTier: 6, enhanceCap: 12 },
];
/** +15는 지역6 클리어로 연다(PRD 2.2) */
export const ENHANCE_MAX = 15;
export const ENHANCE_MAX_UNLOCK_REGION: RegionNo = 6;

/** 현재 강화 상한: 도착한 가장 높은 마을 + 지역6 클리어 여부 */
export function enhanceCap(highestTown: number, region6Cleared: boolean): number {
  if (region6Cleared) return ENHANCE_MAX;
  const t = TOWNS_90D.filter((x) => x.no <= highestTown);
  return t.length ? t[t.length - 1].enhanceCap : TOWNS_90D[0].enhanceCap;
}

export function stageById(id: string): StageDef | undefined {
  for (const r of REGIONS_90D) for (const s of r.stages) if (s.id === id) return s;
  return undefined;
}
